<template>
  <div>
    <TopBar />

    <main>
      <h1 class="mb-8 mt-10 text-center text-39px font-medium text-slate">
        Explore destinations
      </h1>

      <ClientOnly>
        <ExploreMap :destinations="filteredDestinations" :fit-bounds-initial="!!appliedContinents.length" />
      </ClientOnly>

      <div style="max-width: 1392px;" class="mx-auto px-4 pt-6">
        <div class="mb-6 flex overflow-x-scroll md:overflow-x-visible">
          <DropdownFilter
            v-model:applied-items="appliedContinents"
            v-model:selected-items="selectedContinents"
            title="Continents"
            :items="selectContinents"
            :selected-total="selectedTotal"
            dropdown-class="lg:w-100"
          />
          <DropdownFilter
            v-model:applied-items="appliedCountries"
            v-model:selected-items="selectedCountries"
            title="Countries"
            :items="selectCountries"
            :selected-total="selectedTotal"
            dropdown-class="lg:w-128"
          />
          <DropdownFilter
            v-model:applied-items="appliedTravelTypes"
            v-model:selected-items="selectedTravelTypes"
            title="Travel Types"
            :items="travelTypes"
            :selected-total="selectedTotal"
            dropdown-class="lg:w-100"
          />
        </div>

        <div class="mb-4 font-bold text-slate">
          <span v-if="selectedCountriesData.length === 1" class="text-4xl">{{ selectedCountriesData[0].name }}</span>
          <span v-else-if="selectedCountriesData.length > 1" class="text-4xl">Selected countries</span>
          <span v-else class="text-2xl">Popular destinations</span>
        </div>

        <div v-if="destinations.loading" class="mb-10 h-20 spinner" />

        <DestinationsList :destinations="destinationsList" :show-count-default="showCountByDefault" class="mb-12" :class="{ 'countries-selected': selectedCountriesData.length }" />

        <div v-if="showMore" class="mb-6 text-center">
          <a href="#" class="letter-sp15 inline-block rounded border border-gray-dark px-10 py-3 text-lg uppercase text-gray-dark hover:bg-gray-200" @click.prevent="onShowMore">
            <span>Show More</span>
          </a>
        </div>
        <div class="text-center">
          <a href="#" class="letter-sp12 text-sm uppercase text-gray-dark" @click.prevent="goToRandomDest">
            I’m feeling adventurous
          </a>
        </div>
      </div>
    </main>
    <MainFooter />
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import { useAsyncData } from 'nuxt/app'
import travelTypes from '../config/travel-types'
import continents from '../config/continents'
import TopBar from '~/components/layout/TopBar'
import MainFooter from '~/components/layout/MainFooter'
import ExploreMap from '~/components/explore/ExploreMap'
import DestinationsList from '~/components/explore/DestinationsList'
import DropdownFilter from '~/components/shared/DropdownFilter'
import { useDestinationsStore } from '~/stores/destinations'
import { useCountriesStore } from '~/stores/countries'
import { useHead } from '#imports'

export default {
  components: { DropdownFilter, DestinationsList, ExploreMap, TopBar, MainFooter },

  async setup () {
    useHead({
      title: 'Tripexpert - Explore',
      meta: [
        { hid: 'description', name: 'description', content: 'Tripexpert - Explore' },
        { hid: 'og:description', property: 'og:description', content: 'Tripexpert - Explore' },
      ],
    })

    await useAsyncData(() => useDestinationsStore().loadDestinations())
  },

  data: () => ({
    appliedContinents: [],
    appliedCountries: [],
    appliedTravelTypes: [],
    selectedContinents: [],
    selectedCountries: [],
    selectedTravelTypes: [],
    showCountDefault: 8,
    showCountCountryDefault: 9,
    showCount: 14,
    showCountCountry: 15,
    travelTypes,
  }),

  computed: {
    ...mapState(useDestinationsStore, ['destinations']),
    ...mapState(useCountriesStore, {
      countries: s => s.countries.data,
    }),

    showCountByDefault () {
      return this.appliedCountries.length ? this.showCountCountry : this.showCountDefault
    },

    selectCountries () {
      let countries = this.countries
      if (this.appliedContinents.length) {
        countries = countries.filter(c => this.appliedContinents.includes(`${c.continent_id}`))
      }

      countries = countries.map((c) => {
        return {
          id: c.id,
          name: c.name,
          continent_id: c.continent_id,
          count: this.filteredDestinations.filter(d => d.country_id === c.id).length,
        }
      }).filter(c => c.count) // Show only countries that has destinations

      return countries
    },

    selectContinents () {
      return continents
    },

    selectedCountriesData () {
      return this.countries.filter(c => this.appliedCountries.includes(`${c.id}`))
    },

    filteredDestinations () {
      return this.filterDestinations(this.appliedContinents, this.appliedCountries, this.appliedTravelTypes)
    },

    destinationsList () {
      if (this.appliedCountries.length) {
        return this.filteredDestinations.slice(0, this.showCountCountry)
      } else {
        return this.filteredDestinations.slice(0, this.showCount)
      }
    },

    showMore () {
      return this.filteredDestinations.length > this.destinationsList.length
    },

    selectedTotal () {
      return this.filterDestinations(this.selectedContinents, this.selectedCountries, this.selectedTravelTypes).length
    },
  },

  watch: {
    '$route.query': function () {
      this.applyFiltersFromQuery()
    },
  },

  mounted () {
    this.loadCountries()
    this.applyFiltersFromQuery()
  },

  methods: {
    ...mapActions(useCountriesStore, ['loadCountries']),
    ...mapActions(useDestinationsStore, ['loadDestinations']),

    filterDestinations (continents, countries, travelTypes) {
      return this.destinations.data.filter((d) => {
        const hasContinent = ! continents.length || continents.includes(`${d.continent_id}`)
        const hasCountry = ! countries.length || countries.includes(`${d.country_id}`)
        const hasTravelTypes = ! travelTypes.length || travelTypes.every(t => d.travel_type_ids.includes(+t))
        return hasContinent && hasCountry && hasTravelTypes
      })
    },

    randomDestination () {
      return this.destinations.data[Math.floor(Math.random() * this.destinations.data.length)]
    },

    goToRandomDest () {
      this.$router.push(this.randomDestination().path)
    },

    onShowMore () {
      const scrollY = window.scrollY
      if (this.selectedCountriesData.length) {
        this.showCountCountry += 18
      } else {
        this.showCount += 18
      }
      // Workaround to keep window scroll position when adding blocks
      this.$nextTick(() => window.scrollTo(0, scrollY))
      setTimeout(() => window.scrollTo(0, scrollY), 10)
    },

    applyFiltersFromQuery () {
      if (this.$route.query.tt) {
        this.appliedTravelTypes = this.$route.query.tt.split(',')
        this.selectedTravelTypes = [...this.appliedTravelTypes]
      }
      if (this.$route.query.c) {
        this.appliedContinents = this.$route.query.c.split(',')
        this.selectedContinents = [...this.appliedContinents]
      }
    },
  },
}
</script>
